import React, { useEffect, useRef, useState } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import styles from "./DienstleistungDetail.module.css";
import { categories, type Category } from "../data/dienstleistungenData";
import { mediaById } from "../data/dienstleistungenMedia";
import CustomButton from "../components/CustomButton";
import HeroPlate, {
  type Media as HeroMedia,
  type HeroVariant,
} from "../components/HeroPlate";

type SectionKey = "intro" | "steps" | "related";


const IDS = {
  PE: "projektentwicklung",
  PM: "projektmanagement",
  PS: "projektleitung-steuerung",
  VM: "vertrieb-und-vermarktung",
  OG: "objektbetreuung-gewahrleistungsmanagement",
  VB: "veranstaltungsorganisation-fur-bauprojekte",
} as const;

const layoutById: Record<string, SectionKey[]> = {
  [IDS.PE]: ["intro", "steps", "related"],
  [IDS.PM]: ["intro", "steps", "related"],
  [IDS.PS]: ["intro", "steps", "related"],
  [IDS.VM]: ["intro", "steps", "related"],
  [IDS.OG]: ["intro", "steps", "related"],
  [IDS.VB]: ["intro", "steps", "related"],
};


const heroVariantById: Record<string, HeroVariant> = {
  [IDS.PE]: "plate-right",
  [IDS.PM]: "plate-left",
  [IDS.PS]: "plate-right",
  [IDS.VM]: "plate-left",
  [IDS.OG]: "plate-right",
  [IDS.VB]: "plate-left",
};


const toneById: Record<string, "dark" | "light"> = {
  [IDS.PE]: "dark",
  [IDS.PM]: "light",
  [IDS.PS]: "dark",
  [IDS.VM]: "light",
  [IDS.OG]: "dark",
  [IDS.VB]: "light",
};

const sectionTitleById: Record<string, string> = {
  [IDS.PE]: "Unsere Leistungen im Überblick",
  [IDS.PS]: "Unsere Leistungen im Überblick",
  [IDS.PM]: "Unsere Leistungen im Überblick",
  [IDS.VM]: "Unsere Leistungen im Überblick",
  [IDS.OG]: "Unsere Leistungen im Überblick",
  [IDS.VB]: "Unsere Leistungen im Überblick",
};

const DienstleistungDetail: React.FC = () => {
  const { id = "" } = useParams<{ id: string }>();
  const service: Category | undefined = categories.find((c) => c.id === id);
  if (!service) return <Navigate to="/dienstleistungen" replace />;

  const order = layoutById[id] ?? ["intro", "steps", "related"];
  const variant = heroVariantById[id] ?? "plate-left";

  const rootRef = useRef<HTMLElement>(null);

  useEffect(() => {
    document.title = `${service.title} – Dienstleistungen`;
    window.scrollTo({ top: 0 });

    const root = rootRef.current;
    if (!root || window.matchMedia("(prefers-reduced-motion: reduce)").matches)
      return;

    const els = Array.from(root.querySelectorAll<HTMLElement>("[data-reveal]"));
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting)
            (e.target as HTMLElement).classList.add(styles.isVisible);
        });
      },
      {
 
        rootMargin: "0px 0px -6% 0px",
        threshold: 0.08,
      }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [id, service.title]);


  const m = mediaById[id];
  const fallbackStill = "/assets/hero/services.jpg";
  const stillSrc = !m
    ? fallbackStill
    : m.type === "image"
    ? m.src
    : m.poster || fallbackStill;

  const heroMedia: HeroMedia = {
    kind: "image",
    src: stillSrc,
    alt: service.title,
    objectPositionY: "40%",
  };


  const [lead, ...rest] = service.description;
  const related = categories.filter((c) => c.id !== id).slice(0, 3);

 
  const [isMobileBtn, setIsMobileBtn] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia("(max-width: 860px)").matches
      : false
  );
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 860px)");
    const onChange = () => setIsMobileBtn(mq.matches);
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);

  return (
    <main ref={rootRef} className={styles.page}>
 
      <HeroPlate
        media={heroMedia}
        kicker="Dienstleistung"
        title={service.title}
        ctas={[
          {
            text: "Kontakt aufnehmen",
            to: "/contact",
            variant: "filled",
            size: "small",
          },
          {
            text: "← Zurück",
            to: "/dienstleistungen",
            variant: "outline",
            size: "small",
          },
        ]}
        variant={variant}
        tone={toneById[id] ?? "dark"}
        blur={false}
        grain={false}
        vignette={false}
  
        height="clamp(50vh, 62vh, 76vh)"
        maxPlateWidth={760}
        imgPosY="40%"
      />

   
      <section className={styles.section}>
        <div className={styles.container}>
          <div className={styles.main}>
            {order.map((key) => {
              if (key === "intro") {
                return (
                  <section key="intro" className={styles.block} data-reveal>
                    {lead && <p className={styles.lead}>{lead}</p>}
                    {rest.map((p, i) => (
                      <p key={i} className={styles.p}>
                        {p}
                      </p>
                    ))}
                  </section>
                );
              }

              if (key === "steps" && service.items.length > 0) {
                const sectionTitle = sectionTitleById[id] ?? "Leistungen";
                return (
                  <section key="steps" className={styles.block}>
                    <h2 className={`${styles.h2} ${styles.reveal}`} data-reveal>
                      {sectionTitle}
                    </h2>
                    <ol className={styles.steps}>
                      {service.items.map((item, i) => (
                        <li key={item} className={styles.reveal} data-reveal>
                          <span className={styles.dot} aria-hidden="true" />
                          <div className={styles.stepBody}>
                            <h3 className={styles.stepTitle}>{item}</h3>
                          </div>
                        </li>
                      ))}
                    </ol>
                  </section>
                );
              }

              if (key === "related") {
                return (
                  <section
                    key="related"
                    className={`${styles.related} ${styles.reveal}`}
                    data-reveal
                  >
                    <h2 className={styles.h2}>Weitere Schwerpunkte</h2>
                    <div className={styles.relatedGrid}>
                      {related.map((r) => (
                        <Link
                          key={r.id}
                          to={`/dienstleistungen/${r.id}`}
                          className={styles.relatedItem}
                        >
                          <span>{r.title}</span>
                          <em>{r.tagline}</em>
                        </Link>
                      ))}
                    </div>
                  </section>
                );
              }

              return null;
            })}

            <div className={`${styles.bottom} ${styles.reveal}`} data-reveal>
              <div className={styles.bottomRow}>
                <CustomButton
                  to="/dienstleistungen"
                  variant="filled"
                  size={isMobileBtn ? "medium" : "large"}
                  text="← zu allen Leistungen"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default DienstleistungDetail;
